import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { Subject, catchError, finalize, of, switchMap, takeUntil } from 'rxjs';
import { LocationService } from '../../../services/location.service';
import { EmployeeService } from '../../../employee/services/employee.service';
import { LoggerService } from '../../../services/logger.service';
import { AuthService } from '../../../services/auth.service';
import {
  CheckboxMultiDropdownComponent,
  CheckboxMultiDropdownOption,
} from '../../../shared/components/checkbox-multi-dropdown/checkbox-multi-dropdown.component';
import { DateRangePickerComponent } from '../../../shared/components/wi-date-range-picker/wi-date-range-picker.component';
import {
  ScheduledReport,
  ScheduledReportDraft,
  ScheduledReportFormat,
  ScheduledReportFrequencyKind,
  ScheduledReportType,
  createEmptyScheduleDraft,
} from '../../models/scheduled-report.model';

@Component({
  selector: 'app-schedule-report-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatSelectModule,
    CheckboxMultiDropdownComponent,
    DateRangePickerComponent,
  ],
  templateUrl: './schedule-report-modal.component.html',
  styleUrl: './schedule-report-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ScheduleReportModalComponent implements OnInit, OnChanges, OnDestroy {
  private readonly locationService = inject(LocationService);
  private readonly employeeService = inject(EmployeeService);
  private readonly authService = inject(AuthService);
  private readonly logger = inject(LoggerService);
  private readonly destroy$ = new Subject<void>();
  private readonly employeesReload$ = new Subject<string[]>();

  @Input() visible = false;
  @Input() schedule: ScheduledReport | null = null;

  @Output() save = new EventEmitter<{
    draft: ScheduledReportDraft;
    locationOptions: CheckboxMultiDropdownOption[];
    employeeOptions: CheckboxMultiDropdownOption[];
  }>();
  @Output() cancel = new EventEmitter<void>();

  draft: ScheduledReportDraft = createEmptyScheduleDraft();

  locationOptions: CheckboxMultiDropdownOption[] = [];
  employeeOptions: CheckboxMultiDropdownOption[] = [];
  locationsLoading = false;
  employeesLoading = false;

  readonly reportTypeOptions: { label: string; value: ScheduledReportType }[] = [
    { label: 'Shift Report', value: 'shift' },
    { label: 'Cash Report', value: 'cash' },
  ];

  readonly cashFrequencyOptions: { label: string; value: ScheduledReportFrequencyKind }[] = [
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Monthly', value: 'monthly' },
    { label: 'Shift End', value: 'shift_end' },
  ];

  readonly shiftFrequencyOptions: { label: string; value: ScheduledReportFrequencyKind }[] = [
    { label: 'Shift End', value: 'shift_end' },
    { label: 'Daily', value: 'daily' },
  ];

  readonly weekdayOptions: { label: string; value: string }[] = [
    { label: 'Monday', value: 'Mon' },
    { label: 'Tuesday', value: 'Tue' },
    { label: 'Wednesday', value: 'Wed' },
    { label: 'Thursday', value: 'Thu' },
    { label: 'Friday', value: 'Fri' },
    { label: 'Saturday', value: 'Sat' },
    { label: 'Sunday', value: 'Sun' },
  ];

  readonly monthDayOptions: { label: string; value: number }[] = Array.from(
    { length: 28 },
    (_, i) => ({ label: String(i + 1), value: i + 1 })
  );

  readonly formatOptions: { label: string; value: ScheduledReportFormat }[] = [
    { label: 'PDF', value: 'pdf' },
    { label: 'Excel', value: 'excel' },
  ];

  get title(): string {
    return this.schedule ? 'Edit schedule' : 'New schedule';
  }

  get isCashReport(): boolean {
    return this.draft.report_type === 'cash';
  }

  get isShiftReport(): boolean {
    return this.draft.report_type === 'shift';
  }

  get frequencyOptions(): { label: string; value: ScheduledReportFrequencyKind }[] {
    return this.isCashReport ? this.cashFrequencyOptions : this.shiftFrequencyOptions;
  }

  get showDeliveryTime(): boolean {
    return this.draft.frequency_kind !== 'shift_end';
  }

  get showSeparateMailsToggle(): boolean {
    return this.draft.frequency_kind !== 'shift_end';
  }

  get hasNoEmployees(): boolean {
    return !this.employeesLoading && this.employeeOptions.length === 0;
  }

  get canSave(): boolean {
    return this.validationError() === null;
  }

  ngOnInit(): void {
    this.employeesReload$
      .pipe(
        switchMap(locationIds => {
          this.employeesLoading = true;
          return this.employeeService
            .loadEmployees(1, 1000, undefined, {
              status: 'active',
              locations: locationIds.length > 0 ? locationIds : undefined,
            })
            .pipe(
              catchError(err => {
                this.logger.error('Schedule modal: failed to load employees', err);
                return of({ employees: [] });
              }),
              finalize(() => {
                this.employeesLoading = false;
              })
            );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(res => {
        this.employeeOptions = (res.employees ?? []).map(emp => ({
          id: emp.id,
          name: `${emp.first_name} ${emp.last_name}`.trim(),
        }));
        const validIds = new Set(this.employeeOptions.map(opt => opt.id));
        this.draft.employee_ids = this.draft.employee_ids.filter(id => validIds.has(id));
      });

    this.loadLocations();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] || changes['schedule']) {
      if (this.visible) {
        this.resetFromSchedule(this.schedule);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.employeesReload$.complete();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.visible) {
      this.onCancel();
    }
  }

  onOverlayClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.onCancel();
    }
  }

  onCancel(): void {
    this.cancel.emit();
  }

  onClear(): void {
    this.draft = this.newDraft(this.draft.report_type);
    this.reloadEmployees();
  }

  onReportTypeChange(): void {
    this.draft.frequency_kind = 'shift_end';
    this.draft.delivery_time = '';
    this.draft.individual_reports = false;
  }

  onFrequencyChange(): void {
    if (this.draft.frequency_kind === 'shift_end') {
      this.draft.delivery_time = '';
      this.draft.individual_reports = false;
    } else if (!this.draft.delivery_time) {
      this.draft.delivery_time = '08:00';
    }
  }

  onSave(): void {
    if (!this.canSave) {
      return;
    }
    this.save.emit({
      draft: {
        ...this.draft,
        recipient_emails: this.draft.recipient_emails.map(email => email.trim()).filter(Boolean),
      },
      locationOptions: this.locationOptions,
      employeeOptions: this.employeeOptions,
    });
  }

  onLocationIdsChange(ids: string[]): void {
    this.draft.location_ids = [...new Set(ids)];
    this.draft.employee_ids = [];
    this.reloadEmployees();
  }

  onEmployeeIdsChange(ids: string[]): void {
    this.draft.employee_ids = [...new Set(ids)];
  }

  onStartDateChange(date: Date | null): void {
    this.draft.start_date = date;
  }

  addRecipient(): void {
    this.draft.recipient_emails = [...this.draft.recipient_emails, ''];
  }

  removeRecipient(index: number): void {
    if (this.draft.recipient_emails.length <= 1) {
      this.draft.recipient_emails = [''];
      return;
    }
    this.draft.recipient_emails = this.draft.recipient_emails.filter((_, i) => i !== index);
  }

  trackByIndex(index: number): number {
    return index;
  }

  private validationError(): string | null {
    if (!this.draft.report_type) {
      return 'Report type is required.';
    }
    if (!this.draft.format) {
      return 'Format is required.';
    }
    if (!this.draft.start_date) {
      return 'Start date is required.';
    }

    if (!this.draft.frequency_kind) {
      return 'Frequency is required.';
    }
    if (this.draft.frequency_kind === 'weekly' && !this.draft.weekday) {
      return 'Weekday is required.';
    }
    if (this.draft.frequency_kind === 'monthly' && !this.draft.month_day) {
      return 'Day of month is required.';
    }
    if (this.showDeliveryTime && !this.draft.delivery_time) {
      return 'Delivery time is required.';
    }

    const emails = this.draft.recipient_emails.map(e => e.trim()).filter(Boolean);
    if (!emails.length) {
      return 'At least one recipient email is required.';
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emails.some(email => !emailRe.test(email))) {
      return 'Enter valid recipient email addresses.';
    }
    return null;
  }

  private resetFromSchedule(schedule: ScheduledReport | null): void {
    if (!schedule) {
      this.draft = this.newDraft();
      this.reloadEmployees();
      return;
    }

    const startDate = schedule.start_date
      ? new Date(`${schedule.start_date}T12:00:00`)
      : new Date();

    this.draft = {
      id: schedule.id,
      report_type: schedule.report_type,
      location_ids: [...(schedule.location_ids || [])],
      employee_ids: [...(schedule.employee_ids || [])],
      frequency_kind: schedule.frequency.kind,
      weekday: schedule.frequency.kind === 'weekly' ? schedule.frequency.weekday : 'Mon',
      month_day: schedule.frequency.kind === 'monthly' ? schedule.frequency.day : 1,
      delivery_time:
        schedule.frequency.kind === 'shift_end' ? '' : schedule.delivery_time || '08:00',
      individual_reports:
        schedule.frequency.kind === 'shift_end' ? false : !!schedule.individual_reports,
      format: schedule.format,
      start_date: startDate,
      recipient_emails:
        schedule.recipient_emails?.length > 0
          ? [...schedule.recipient_emails]
          : [this.adminEmail() || ''],
    };
    this.reloadEmployees();
  }

  private newDraft(reportType: ScheduledReportType = 'shift'): ScheduledReportDraft {
    return createEmptyScheduleDraft(reportType, this.adminEmail());
  }

  private adminEmail(): string {
    const email = this.authService.user()?.email;
    return typeof email === 'string' ? email.trim() : '';
  }

  private reloadEmployees(): void {
    const locationIds =
      this.draft.location_ids.length > 0
        ? this.draft.location_ids
        : this.locationOptions.map(opt => opt.id);
    this.employeesReload$.next(locationIds);
  }

  private loadLocations(): void {
    this.locationsLoading = true;
    this.locationService
      .getLocations()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.locationsLoading = false;
        })
      )
      .subscribe({
        next: response => {
          if (response?.status && Array.isArray(response.data)) {
            this.locationOptions = this.locationService.getLocationOptions().map(opt => ({
              id: opt.value,
              name: opt.label,
            }));
          } else {
            this.locationOptions = [];
          }
          this.reloadEmployees();
        },
        error: err => {
          this.logger.error('Schedule modal: failed to load locations', err);
          this.locationOptions = [];
          this.employeeOptions = [];
        },
      });
  }
}
